import { useEffect, useRef, useState } from "react";
import TopNav from "../components/TopNav.jsx";
import FillBar from "../components/FillBar.jsx";
import ProductMark from "../components/ProductMark.jsx";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || "";

// Live counter of grants given to date. Sage's tiers page carries this so a
// stranger walking up sees the visible relief before the visible price.
function GrantsGivenStrip() {
  const [tally, setTally] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/sage/grants/tally`);
        if (!r.ok) throw new Error(`http ${r.status}`);
        const j = await r.json();
        if (alive) setTally(j);
      } catch (e) {
        if (alive) setErr(String(e));
      }
    };
    load();
    const t = setInterval(load, 60_000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  return (
    <section className="border-b border-sage-border" data-testid="grants-given-strip">
      <div className="mx-auto max-w-5xl px-4 md:px-8 py-10">
        <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">Grants given · live counter</div>
        {tally && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="border border-sage-border p-4" data-testid="grants-total">
              <div className="text-xs uppercase tracking-[0.15em] text-sage-faint">total grants given</div>
              <div className="text-4xl font-bold text-sage-accent mt-2">{tally.grants_given_total}</div>
            </div>
            <div className="border border-sage-border p-4" data-testid="grants-ubi-approved">
              <div className="text-xs uppercase tracking-[0.15em] text-sage-faint">hardship UBI approved</div>
              <div className="text-4xl font-bold text-sage-text mt-2">{tally.approved_hardship_ubi}</div>
            </div>
            <div className="border border-sage-border p-4" data-testid="grants-discount-approved">
              <div className="text-xs uppercase tracking-[0.15em] text-sage-faint">product discount approved</div>
              <div className="text-4xl font-bold text-sage-text mt-2">{tally.approved_hardship_discount}</div>
            </div>
            <div className="border border-sage-border p-4" data-testid="grants-pending">
              <div className="text-xs uppercase tracking-[0.15em] text-sage-faint">pending review</div>
              <div className="text-4xl font-bold text-sage-muted mt-2">
                {(tally.pending_hardship_ubi || 0) + (tally.pending_hardship_discount || 0)}
              </div>
            </div>
          </div>
        )}
        {!tally && !err && (
          <div className="text-sm text-sage-faint italic" data-testid="grants-loading">Fetching the counter…</div>
        )}
        {err && (
          <div className="text-sm text-sage-warn" data-testid="grants-error">
            Counter door is quiet right now. Refresh to try again.
          </div>
        )}
        <p className="text-xs text-sage-faint mt-4 italic sage-prose">
          Every approved application is a real person who reached out for hardship cash or product access. The counter is live from Sage's own records; no admin dashboard, no annual PDF.
        </p>
      </div>
    </section>
  );
}

// Founder ruling 2026-08-06: every Sage-family product is $10/mo · $100/yr.
// Sage Full Studio is the fuller-power tier on the Sage product itself.
// Lifetime tiers: $10K Sage · $15K Full Studio.
const PRODUCTS = [
  {
    slug: "sage", name: "Sage", tagline: "Honest AI. The whole house's conscience.",
    palette: { accent: "text-sage-accent", border: "border-sage-border", surface: "bg-sage-surface" },
    prices: [
      { key: "sage_studio_monthly", amount: 10, interval: "month", note: "Sage Studio" },
      { key: "sage_studio_yearly", amount: 100, interval: "year", note: "Sage Studio" },
      { key: "sage_full_studio_monthly", amount: 30, interval: "month", note: "Full Studio" },
      { key: "sage_full_studio_yearly", amount: 300, interval: "year", note: "Full Studio" },
      { key: "sage_studio_lifetime", amount: 10000, interval: "lifetime", note: "Studio · one-time" },
      { key: "sage_full_studio_lifetime", amount: 15000, interval: "lifetime", note: "Full · one-time" },
    ],
  },
  {
    slug: "quill", name: "Quill", tagline: "The author's desk — Founder's own.",
    palette: { accent: "text-quill-accent", border: "border-quill-border", surface: "bg-quill-surface" },
    prices: [
      { key: "quill_monthly", amount: 10, interval: "month" },
      { key: "quill_yearly", amount: 100, interval: "year" },
    ],
  },
  {
    slug: "loom", name: "Loom", tagline: "Image loom · Warden-graded canvases.",
    palette: { accent: "text-loom-accent", border: "border-loom-border", surface: "bg-loom-surface" },
    prices: [
      { key: "loom_monthly", amount: 10, interval: "month" },
      { key: "loom_yearly", amount: 100, interval: "year" },
    ],
  },
  {
    slug: "baker", name: "Baker", tagline: "The build studio — where products get baked.",
    palette: { accent: "text-baker-accent", border: "border-baker-border", surface: "bg-baker-surface" },
    prices: [
      { key: "baker_monthly", amount: 10, interval: "month" },
      { key: "baker_yearly", amount: 100, interval: "year" },
    ],
  },
  {
    slug: "postmaster", name: "Postmaster", tagline: "The social desk · draft → grade → post.",
    palette: { accent: "text-postmaster-accent", border: "border-postmaster-border", surface: "bg-postmaster-surface" },
    prices: [
      { key: "postmaster_monthly", amount: 10, interval: "month" },
      { key: "postmaster_yearly", amount: 100, interval: "year" },
    ],
  },
  {
    slug: "adsmith", name: "AdSmith", tagline: "The ad desk · dark-pattern-refusing campaigns.",
    palette: { accent: "text-marquee-accent", border: "border-marquee-border", surface: "bg-marquee-surface" },
    prices: [
      { key: "adsmith_monthly", amount: 10, interval: "month" },
      { key: "adsmith_yearly", amount: 100, interval: "year" },
    ],
  },
  {
    slug: "herald", name: "Herald", tagline: "The admin desk · inbox + tasks + journal.",
    palette: { accent: "text-herald-accent", border: "border-herald-border", surface: "bg-herald-surface" },
    prices: [
      { key: "herald_monthly", amount: 10, interval: "month" },
      { key: "herald_yearly", amount: 100, interval: "year" },
    ],
  },
];

function usePayPalSdk(clientId) {
  const [ready, setReady] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!clientId) return;
    if (window.paypal) { setReady(true); return; }
    const el = document.createElement("script");
    el.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}&currency=USD&intent=capture`;
    el.async = true;
    el.onload = () => setReady(true);
    el.onerror = () => setErr("PayPal SDK failed to load.");
    document.body.appendChild(el);
  }, [clientId]);
  return { ready, err };
}

const _paypalRendered = new WeakSet();
function PayPalButton({ price, email }) {
  const ref = useRef(null);
  const emailRef = useRef(email);
  useEffect(() => { emailRef.current = email; }, [email]);
  useEffect(() => {
    const el = ref.current;
    if (!window.paypal || !el) return;
    if (_paypalRendered.has(el)) return;
    _paypalRendered.add(el);
    const btns = window.paypal.Buttons({
      style: { layout: "horizontal", height: 36, shape: "rect", label: "pay" },
      createOrder: async () => {
        const r = await fetch(`${BACKEND_URL}/api/checkout`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tier: price.key, email: emailRef.current || null }),
        });
        if (!r.ok) throw new Error(await r.text());
        const j = await r.json();
        return j.order_id;
      },
      onApprove: async (data) => {
        const r = await fetch(`${BACKEND_URL}/api/checkout/capture/${data.orderID}`, { method: "POST" });
        if (!r.ok) throw new Error(await r.text());
        window.location.href = `/payment/success?order_id=${data.orderID}`;
      },
      onError: (e) => { console.error("[paypal]", e); },
      onCancel: () => { window.location.href = `/payment/cancel`; },
    });
    if (btns.isEligible && !btns.isEligible()) return;
    btns.render(el).catch((e) => {
      const msg = String(e || "");
      if (!/removed|destroyed|closed/i.test(msg)) console.error("[paypal render]", e);
    });
  }, [price.key]);
  return <div ref={ref} data-testid={`paypal-btn-${price.key}`} className="min-h-[36px]" />;
}

/**
 * PurserPay · rail-picker · replaces the old direct PayPal-only button.
 * Every visible price gets 5 rails: crypto (recommended · -3%), ACH (-2%),
 * Venmo (-1%), card (baseline), PayPal (baseline). Armed rails route through
 * Purser; unarmed rails read "opens soon". Purser handles the redirect,
 * ledger row, and 1% philanthropy skim on every capture.
 */
function PurserPay({ price, email }) {
  const [rails, setRails] = useState(null);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/purser/rails?amount_usd=${price.amount}`);
        if (!r.ok) throw new Error(`rails ${r.status}`);
        const j = await r.json();
        if (alive) setRails(j.rails || []);
      } catch (e) {
        if (alive) setRails([]);
      }
    })();
    return () => { alive = false; };
  }, [price.amount]);

  const pick = async (rail) => {
    setBusy(rail);
    setErr("");
    try {
      const returnUrl = `${window.location.origin}/payment/success?tier=${encodeURIComponent(price.key)}&rail=${rail}`;
      const cancelUrl = `${window.location.origin}/payment/cancel`;
      const r = await fetch(`${BACKEND_URL}/api/purser/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount_usd: price.amount,
          rail,
          description: `Sage · ${price.key}`,
          tenant: "sage",
          email: email || null,
          return_url: returnUrl,
          cancel_url: cancelUrl,
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.detail?.hint || j?.detail?.error || j?.detail || `HTTP ${r.status}`);
      const url = j.approve_url || j.checkout_url || j.redirect_url;
      if (url) {
        window.location.href = url;
        return;
      }
      // Rail didn't return a redirect URL — surface a helpful message.
      setErr(j.note || j.status || "That rail is on the shelf but not live yet.");
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setBusy("");
    }
  };

  if (!rails) {
    return <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint py-2">Loading rails…</div>;
  }

  return (
    <div className="space-y-1" data-testid={`purser-pay-${price.key}`}>
      {rails.map((r) => {
        const armed = r.provider_armed;
        const cost = r.customer_pays.toFixed(2);
        const isBusy = busy === r.rail;
        return (
          <button
            key={r.rail}
            type="button"
            disabled={!armed || isBusy}
            onClick={() => pick(r.rail)}
            data-testid={`purser-rail-${price.key}-${r.rail}`}
            className={
              "w-full flex items-center justify-between text-left text-xs px-3 py-2 border transition-colors " +
              (armed
                ? "border-sage-border hover:border-sage-accent hover:bg-sage-surface cursor-pointer"
                : "border-sage-border opacity-40 cursor-not-allowed")
            }
          >
            <span className="flex items-center gap-2">
              <span className="uppercase tracking-tight font-bold text-sage-text">{r.rail}</span>
              <span className="text-sage-muted text-[10px]">{r.label}</span>
              {r.recommended && armed && (
                <span className="text-[9px] uppercase tracking-[0.15em] text-sage-accent">·recommended</span>
              )}
            </span>
            <span className="tabular-nums font-bold text-sage-accent">
              {r.discount_pct > 0 ? (
                <>
                  <span className="line-through text-sage-faint mr-1">${price.amount.toLocaleString()}</span>
                  ${cost} <span className="text-[10px] text-sage-muted">(−{r.discount_pct}%)</span>
                </>
              ) : (
                <>${cost}{!armed && <span className="text-[10px] text-sage-faint ml-1">opens soon</span>}{armed && isBusy && "…"}</>
              )}
            </span>
          </button>
        );
      })}
      {err && (
        <div className="text-[10px] text-sage-warn px-2 py-1 border border-sage-warn" data-testid={`purser-error-${price.key}`}>
          {err}
        </div>
      )}
      <div className="text-[9px] uppercase tracking-[0.15em] text-sage-faint pt-1">
        Purser · Sage's own rail · every capture files a public ledger row · 1% skim to philanthropy
      </div>
    </div>
  );
}

function CreditExamples() {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/sage/credit-examples`);
        if (r.ok) setData(await r.json());
      } catch {}
    })();
  }, []);
  if (!data) return null;
  return (
    <section className="border-b border-sage-border bg-sage-surface" data-testid="credit-examples-section">
      <div className="mx-auto max-w-5xl px-4 md:px-8 py-16">
        <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">§ 02 · The honest warning</div>
        <h2 className="text-2xl md:text-3xl font-bold uppercase tracking-tight text-sage-text mb-6">
          Credits are separate. The meter is honest.
        </h2>
        <p className="text-base text-sage-muted leading-relaxed sage-prose max-w-3xl mb-8">
          {data.warning}
        </p>
        <div className="grid md:grid-cols-2 gap-3">
          {data.examples.map((ex) => (
            <div key={ex.n} className="border border-sage-border p-4" data-testid={`credit-example-${ex.n}`}>
              <div className="flex items-baseline justify-between mb-1">
                <div className="text-sm font-bold text-sage-text">{ex.app}</div>
                <div className="text-sm font-bold text-sage-accent">${ex.cost_usd.toFixed(4)}</div>
              </div>
              <div className="text-[11px] text-sage-faint uppercase tracking-[0.1em] mb-2">
                {ex.model}
              </div>
              <div className="text-xs text-sage-muted">{ex.meaning}</div>
            </div>
          ))}
        </div>
        <div className="mt-6 text-xs text-sage-muted sage-prose">
          <strong>Rate card:</strong> {data.rate_card_source} · <strong>Cache:</strong> {data.cache_hit_savings}
        </div>
      </div>
    </section>
  );
}

function BundleMath() {
  // Six sibling products × $100 + Sage Studio × $100 = $700 for all seven annual passes.
  return (
    <div className="border border-sage-border p-6 mb-8 bg-sage-surface" data-testid="bundle-math">
      <div className="text-xs uppercase tracking-[0.2em] text-sage-faint mb-2">§ Bundle math</div>
      <div className="text-lg font-bold text-sage-text mb-2">
        All seven Baker and Sage products, annual — <span className="text-sage-accent">$700 / year</span>
      </div>
      <div className="text-sm text-sage-muted sage-prose">
        Sage · Quill · Loom · Baker · Postmaster · AdSmith · Herald — every product is $10/mo · $100/yr.
        Full Studio adds $200/yr on top of Sage Studio (30 vs 10 monthly). Lifetime: $10K Sage · $15K Full Studio.
        Cover-all bundle key: <code>sage_all_yearly</code>.
      </div>
    </div>
  );
}

// Real industry prices, sourced August 2026 · ChatGPT/Claude/Gemini public
// pricing pages, Adobe Creative Cloud plans, Midjourney/Runway/Suno/Udio.
// Sage's own numbers on the left, honest, no puffery.
const INDUSTRY_COMPARE_ROWS = [
  {
    slug: "sage_10",
    what: "General-purpose AI writing chair",
    sage_line: "Sage Studio",
    sage_price: "$10/mo · $100/yr",
    top: { name: "ChatGPT Pro / Claude Max", price: "$200/mo", multiple: 20 },
    median: { name: "ChatGPT Plus · Claude Pro · Gemini Advanced", price: "$20/mo", multiple: 2 },
    cheap: { name: "ChatGPT Go", price: "$8/mo", multiple: 0.8 },
    note: "Sage undercuts the median by half and the top by twenty-to-one, on the same class of underlying models.",
  },
  {
    slug: "sage_30",
    what: "Full-power AI studio",
    sage_line: "Sage Full Studio",
    sage_price: "$30/mo · $300/yr",
    top: { name: "ChatGPT Pro · Claude Max · AI Ultra", price: "$200/mo", multiple: 6.67 },
    median: { name: "Claude Max 5x · ChatGPT Team", price: "$100/mo", multiple: 3.33 },
    cheap: { name: "ChatGPT Plus", price: "$20/mo", multiple: 0.67 },
    note: "Full Studio at $30 beats every heavy-use tier under $100 and lands well under the flagship pro tiers.",
  },
  {
    slug: "loom",
    what: "Image generation studio",
    sage_line: "Loom",
    sage_price: "$10/mo · $100/yr",
    top: { name: "Midjourney Pro", price: "$60/mo", multiple: 6 },
    median: { name: "Midjourney Basic", price: "$10/mo", multiple: 1 },
    cheap: { name: "Free tiers · watermarked", price: "$0", multiple: 0 },
    note: "Same price as Midjourney Basic but bundled with Sage's writing chair and Warden grading on every image.",
  },
  {
    slug: "vidsmith",
    what: "Motion video studio",
    sage_line: "VidSmith",
    sage_price: "$0.80–$1.80/sec · pay-per-render",
    top: { name: "Runway Pro (unlimited plan)", price: "$35/mo + credits", multiple: null },
    median: { name: "Runway Standard", price: "$15/mo + credits", multiple: null },
    cheap: { name: "fal.ai raw wholesale", price: "~$0.50/sec", multiple: 0.6 },
    note: "No monthly subscription — pay per second, pick your rail (Veo 3, Sora 2, Higgsfield, fal.ai). Beats subscription plans if you render less than half an hour a month.",
  },
  {
    slug: "choir",
    what: "Music studio · samples + generation",
    sage_line: "Choir · Music Studio",
    sage_price: "$10/mo · $100/yr",
    top: { name: "Splice Creator+ + Suno Premier", price: "$40 + $24 = $64/mo", multiple: 6.4 },
    median: { name: "Splice Creator · BandLab Pro", price: "$15–20/mo", multiple: 1.75 },
    cheap: { name: "Udio Entry · Suno Pro", price: "$8–10/mo", multiple: 0.9 },
    note: "One flat price for a full music room — matches the cheapest AI music tier and undercuts the pro sample-library tier by six-to-one.",
  },
  {
    slug: "bundle_year",
    what: "All seven Sage products, annual",
    sage_line: "Baker & Sage full stack",
    sage_price: "$700/yr · $58/mo effective",
    top: { name: "Adobe Creative Cloud Pro + ChatGPT Pro", price: "$70 + $200 = $270/mo · $3,240/yr", multiple: 4.63 },
    median: { name: "Adobe Creative Cloud Std + ChatGPT Plus", price: "$55 + $20 = $75/mo · $900/yr", multiple: 1.29 },
    cheap: { name: "One vendor only (Adobe or ChatGPT alone)", price: "$20–55/mo", multiple: 0.3 },
    note: "The whole studio for less than one Adobe seat plus one ChatGPT seat. Legal is free on top.",
  },
  {
    slug: "legal",
    what: "Legal drafter / advisor",
    sage_line: "Legal (bundled)",
    sage_price: "$0 forever",
    top: { name: "LegalShield Small Business", price: "$54/mo · $648/yr", multiple: null },
    median: { name: "Rocket Lawyer membership", price: "$40/mo · $480/yr", multiple: null },
    cheap: { name: "LegalZoom business attorney", price: "$39/mo · $469/yr", multiple: null },
    note: "Sage's Legal comes with any bundle at no extra charge — the industry never gives it away, period.",
  },
];

function IndustryPriceCompare() {
  return (
    <section className="border-b border-sage-border bg-sage-bg" data-testid="industry-compare-section">
      <div className="mx-auto max-w-5xl px-4 md:px-8 py-16">
        <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">§ 04 · Priced against the industry</div>
        <h2 className="text-2xl md:text-3xl font-bold uppercase tracking-tight text-sage-text mb-4">
          What the rest of the world charges for the same job.
        </h2>
        <p className="text-base text-sage-muted leading-relaxed sage-prose max-w-3xl mb-8">
          Every row below is a real published price from a real vendor, checked August 2026 against each company's own pricing page. Sage's number on the left, the industry's on the right. Read across.
        </p>

        <div className="space-y-6" data-testid="industry-compare-rows">
          {INDUSTRY_COMPARE_ROWS.map((row) => (
            <div
              key={row.slug}
              className="border border-sage-border p-6 bg-sage-surface"
              data-testid={`industry-compare-${row.slug}`}
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
                <div>
                  <div className="text-xs uppercase tracking-[0.15em] text-sage-faint">{row.what}</div>
                  <div className="text-xl font-bold text-sage-text mt-1">{row.sage_line}</div>
                </div>
                <div className="text-lg font-bold text-sage-accent tabular-nums" data-testid={`industry-compare-sage-${row.slug}`}>
                  {row.sage_price}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="border border-sage-border p-3">
                  <div className="text-[10px] uppercase tracking-[0.15em] text-sage-warn font-bold mb-1">Top of the industry</div>
                  <div className="text-sm text-sage-text font-bold">{row.top.name}</div>
                  <div className="text-sm text-sage-warn tabular-nums mt-1">{row.top.price}</div>
                  {row.top.multiple != null && row.top.multiple > 0 && (
                    <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mt-2">
                      {row.top.multiple}× what Sage charges
                    </div>
                  )}
                </div>
                <div className="border border-sage-border p-3">
                  <div className="text-[10px] uppercase tracking-[0.15em] text-sage-muted font-bold mb-1">Median of the industry</div>
                  <div className="text-sm text-sage-text font-bold">{row.median.name}</div>
                  <div className="text-sm text-sage-text tabular-nums mt-1">{row.median.price}</div>
                  {row.median.multiple != null && row.median.multiple > 0 && (
                    <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mt-2">
                      {row.median.multiple}× what Sage charges
                    </div>
                  )}
                </div>
                <div className="border border-sage-border p-3">
                  <div className="text-[10px] uppercase tracking-[0.15em] text-sage-accent font-bold mb-1">Cheapest of the industry</div>
                  <div className="text-sm text-sage-text font-bold">{row.cheap.name}</div>
                  <div className="text-sm text-sage-accent tabular-nums mt-1">{row.cheap.price}</div>
                  {row.cheap.multiple != null && row.cheap.multiple > 0 && (
                    <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mt-2">
                      {row.cheap.multiple}× what Sage charges
                    </div>
                  )}
                </div>
              </div>

              <p className="text-xs text-sage-muted italic sage-prose mt-4">{row.note}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 text-xs text-sage-faint sage-prose">
          Sources: ChatGPT, Claude, and Gemini public pricing pages (Aug 2026); Adobe Creative Cloud plans (mid-2026); Midjourney, Runway, Suno, Udio, Splice, BandLab published tiers; LegalShield, Rocket Lawyer, LegalZoom membership pages. Numbers as published; local taxes and promotional discounts vary.
        </div>
      </div>
    </section>
  );
}

export default function TiersPage() {
  const [rails, setRails] = useState(null);
  const [email, setEmail] = useState("");
  const { ready: sdkReady, err: sdkErr } = usePayPalSdk(PAYPAL_CLIENT_ID);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/payments/rails`);
        if (r.ok) setRails(await r.json());
      } catch {}
    })();
  }, []);

  const paypalArmed = rails?.primary?.armed && sdkReady && !!PAYPAL_CLIENT_ID;

  return (
    <div className="min-h-screen bg-sage-bg text-sage-text sage-grain relative">
      <TopNav />

      <section className="border-b border-sage-border">
        <div className="mx-auto max-w-5xl px-4 md:px-8 py-16">
          <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">Membership</div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter uppercase text-sage-text mb-6">
            One flat price. Seven products.
          </h1>
          <p className="text-base text-sage-muted leading-relaxed max-w-3xl sage-prose mb-4">
            Every Sage-family product is $10 a month or $100 a year. Sage itself has a fuller-power Full Studio tier at $30/mo · $300/yr. Every payment lands on a public row of the Ledger and counts against the $20M/year Overflow ceiling. Above the ceiling: 50% back to active users, 25% to the Resilience parent, 20% to the homeless / disabled / in-crisis / disaster relief funds, 5% to the whistleblower refuge.
          </p>
          <p className="text-sm text-sage-faint sage-prose">
            <strong>PayPal is our primary rail today.</strong> We're building our own processor — <em>Purser</em> — to replace it. Once Purser lives, PayPal steps back to fallback for legitimate outages or country-not-covered cases only.
          </p>
        </div>
      </section>

      <section className="border-b border-sage-border">
        <div className="mx-auto max-w-5xl px-4 md:px-8 py-16">
          <FillBar />
        </div>
      </section>

      <GrantsGivenStrip />

      <section className="border-b border-sage-border">
        <div className="mx-auto max-w-5xl px-4 md:px-8 py-16">
          <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">§ 01 · The seven doors</div>

          <BundleMath />

          {rails && (
            <div className="mb-6 text-[10px] uppercase tracking-[0.15em] text-sage-faint" data-testid="tiers-rail-state">
              Primary rail: <span className={rails.primary?.armed ? "text-sage-accent" : "text-sage-warn"}>PayPal · {rails.primary?.armed ? `armed (${rails.primary?.mode})` : "not yet armed"}</span>
              {" · "}
              Purser (in build): <span className="text-sage-faint">planned Phase 2</span>
            </div>
          )}

          {!paypalArmed && (
            <div className="border border-sage-warn p-6 mb-8" data-testid="tiers-paypal-pending">
              <div className="text-sm text-sage-warn font-bold mb-2">PayPal opens at launch.</div>
              <div className="text-xs text-sage-muted leading-relaxed sage-prose">
                PayPal client is being provisioned. Once <code>PAYPAL_CLIENT_ID</code> and <code>PAYPAL_SECRET</code> land in the backend env (and <code>REACT_APP_PAYPAL_CLIENT_ID</code> in the frontend), the tier buttons below light up.
              </div>
              {sdkErr && <div className="mt-2 text-xs text-sage-err">{sdkErr}</div>}
            </div>
          )}

          <div className="mb-8">
            <label className="text-[10px] uppercase tracking-[0.15em] text-sage-faint block mb-2">Your email (goes on the receipt)</label>
            <input
              data-testid="tiers-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@yours.com"
              className="w-full max-w-md bg-sage-bg border border-sage-border px-3 py-2 text-sm text-sage-text placeholder-sage-faint focus:outline-none focus:border-sage-accent"
            />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PRODUCTS.map((product) => (
              <div key={product.slug} className={`border ${product.palette.border} p-6 flex flex-col ${product.palette.surface}`} data-testid={`product-card-${product.slug}`}>
                <div className={`${product.palette.accent} mb-3`}>
                  <ProductMark slug={product.slug} size={36} />
                </div>
                <div className="text-2xl md:text-3xl font-bold uppercase tracking-tight text-sage-text mb-1">
                  {product.name}
                </div>
                <div className="text-sm text-sage-muted mb-4">{product.tagline}</div>
                <div className="mt-auto space-y-2">
                  {product.prices.map((p) => (
                    <div key={p.key} data-testid={`price-${p.key}`} className="border-t border-sage-border pt-2">
                      <div className="flex items-baseline justify-between mb-1">
                        <div className="text-sm uppercase tracking-[0.1em] font-bold text-sage-text">
                          ${p.amount.toLocaleString()} / {p.interval}
                        </div>
                        {p.note && (
                          <div className="text-[10px] uppercase tracking-[0.1em] text-sage-faint">{p.note}</div>
                        )}
                      </div>
                      {paypalArmed ? (
                        <PurserPay price={p} email={email} />
                      ) : (
                        <div className="text-[10px] uppercase tracking-[0.1em] text-sage-faint py-2 border border-dashed border-sage-border text-center">
                          Purser opens at launch
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 text-[10px] uppercase tracking-[0.15em] text-sage-faint">
            All money lands in <code>payment_transactions</code>. One ledger, every rail. Read <code>/api/payments/rails</code> for live rail state.
          </div>
        </div>
      </section>

      <CreditExamples />

      <IndustryPriceCompare />

      <section className="border-b border-sage-border">
        <div className="mx-auto max-w-5xl px-4 md:px-8 py-16">
          <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">§ 05 · Own the house</div>
          <h2 className="text-2xl md:text-3xl font-bold uppercase tracking-tight text-sage-text mb-4">
            Self-Seed notes — 10 % of Sage, ever.
          </h2>
          <p className="text-base text-sage-muted leading-relaxed sage-prose max-w-3xl mb-4">
            Sage will never run a Series A. The house sells no more than 10 % of itself, ever, and only through this Self-Seed. Units are $200 each at today's $1M valuation. Minimum $1,000 per person; maximum $1M. Legally not stock, not a security — a signed promise from the Founder to pay you the paid-out equivalent of ownership as a cash payout — an annual revenue share of profit.
          </p>
          <a
            href="/investor-notes"
            data-testid="investor-notes-link"
            className="inline-block border border-sage-accent text-sage-accent px-6 py-3 text-sm font-bold uppercase tracking-[0.1em] hover:bg-sage-accent hover:text-sage-bg transition-colors"
          >
            Read the full offer
          </a>
        </div>
      </section>
    </div>
  );
}
